'use client'

// ---------------------------------------------------------------------------
// Workspace context — WHOSE world the workspace surfaces show.
//
// One active context at a time: 'personal' or a team org id. On URL-scoped
// pages (/projects, /team/<slug>/…, /settings/*) the URL is the source of
// truth and the page syncs this store to it; elsewhere the store is the
// memory of the last context (persisted so the app reopens where you left
// off).
//
// Hydration safety: the initial value is always 'personal' — identical to
// the server render — and the persisted value is applied post-mount via
// hydrate() (called from AppHeader). setContext marks the store hydrated so
// a URL-derived context set during mount can never be clobbered by a stale
// localStorage read that resolves later.
//
// Switching is not instant: the picked workspace only takes effect when its
// route commits, and until then the previous workspace's page is still on
// screen. beginSwitch records that in-flight pick so AppShell can cover the
// stale content instead of passing it off as the new workspace (ENG-723).
// ---------------------------------------------------------------------------

import { create } from 'zustand'

const KEY = 'martini-workspace-context'

type WorkspaceContextState = {
  /** 'personal' or a team org id. */
  contextId: string
  setContext: (id: string) => void
  /** True once the persisted value was applied (or an explicit set won). */
  hydrated: boolean
  /** Apply the persisted context after mount. Idempotent; loses to setContext. */
  hydrate: () => void
  /** Workspace the user picked whose route hasn't committed yet; null when settled. */
  pendingContextId: string | null
  /**
   * Pathname this switch is navigating TO. A switch is settled by arriving
   * here specifically — not by the path merely changing — so a slower earlier
   * switch committing can't take down the overlay for a newer one. Supplied by
   * the caller rather than read from `window.location` so it and the overlay's
   * `usePathname()` are the same source of truth.
   */
  switchTargetPathname: string | null
  /** Context to restore if the switch is abandoned rather than landing. */
  previousContextId: string | null
  /** Adopt the picked workspace and mark the navigation to it in flight. */
  beginSwitch: (id: string, targetPathname: string) => void
  /** Settle the switch where it landed: drop the in-flight mark, keep the pick. */
  endSwitch: () => void
  /**
   * Give up on the switch and put the workspace back. Used when the navigation
   * never lands (Back during the switch, or the stuck backstop) — the user is
   * still on the origin page, so leaving the chip on the picked workspace would
   * name a workspace whose content was never shown.
   */
  abandonSwitch: () => void
  /** Pending "open the Create Team dialog" request from the navbar switcher.
      A flag (not a counter compared against mount state) so it survives the
      navigation to /projects and is consumed exactly once. */
  createTeamRequested: boolean
  requestCreateTeam: () => void
  consumeCreateTeamRequest: () => void
}

export const useWorkspaceContext = create<WorkspaceContextState>((set, get) => ({
  contextId: 'personal',
  hydrated: false,
  pendingContextId: null,
  switchTargetPathname: null,
  previousContextId: null,
  setContext: id => {
    if (typeof window !== 'undefined') localStorage.setItem(KEY, id)
    // A page only calls this once its route is rendering, so any in-flight
    // switch has landed by definition. In practice a switch is settled by the
    // overlay's pathname guard first — the workspace surfaces call this only
    // when the value differs and beginSwitch has already set it — so this is
    // the backstop for a route that lands on a DIFFERENT workspace than the
    // one picked (e.g. a stale team slug bouncing to /projects). The personal
    // admin layout is the exception: it calls this unconditionally, because
    // there the no-op case is the bug (ENG-727). Settling an in-flight switch
    // from there is right either way — picking Personal off an admin surface
    // TARGETS /settings/account, so mounting it means the switch landed, and
    // arriving from any other pending switch means the user left it behind.
    set({ contextId: id, hydrated: true, pendingContextId: null, switchTargetPathname: null, previousContextId: null })
  },
  hydrate: () => {
    if (get().hydrated || typeof window === 'undefined') return
    set({ contextId: localStorage.getItem(KEY) || 'personal', hydrated: true })
  },
  beginSwitch: (id, targetPathname) => {
    if (typeof window !== 'undefined') localStorage.setItem(KEY, id)
    set(state => ({
      contextId: id,
      hydrated: true,
      pendingContextId: id,
      switchTargetPathname: targetPathname,
      // Keep the FIRST pre-switch context across back-to-back picks, so
      // abandoning restores what was actually last on screen.
      previousContextId: state.previousContextId ?? state.contextId,
    }))
  },
  endSwitch: () => set({ pendingContextId: null, switchTargetPathname: null, previousContextId: null }),
  abandonSwitch: () => {
    const { previousContextId } = get()
    if (previousContextId !== null && typeof window !== 'undefined') localStorage.setItem(KEY, previousContextId)
    set(state => ({
      contextId: state.previousContextId ?? state.contextId,
      pendingContextId: null,
      switchTargetPathname: null,
      previousContextId: null,
    }))
  },
  createTeamRequested: false,
  requestCreateTeam: () => set({ createTeamRequested: true }),
  consumeCreateTeamRequest: () => set({ createTeamRequested: false }),
}))
